import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { getActivityTracker } from "@endpoints/activityTracker/activityTracker.service";
import SkeletonTrackerList from "./components/SkeletonTrackerList";
import TrackIcon from "@assets/svg/TrackIcon";
import { EnumActivityTracker } from "@utils/constants";
import { IActivityTracker } from "@endpoints/types/activityTracker.type";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

const BoldText = ({ children }) => {
  return <span className="text-[#3DCCDE] font-semibold w-max">{children}</span>;
};
const BaseText = ({ children }) => {
  return <span className="font-semibold text-gray-500">{children}</span>;
};

function getDescription(type: EnumActivityTracker, data: IActivityTracker) {
  switch (type) {
    case EnumActivityTracker.CANDIDATE_APPLIED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>
            {data?.candidateId?.firstName} {data?.candidateId?.lastName}
          </BoldText>
          applied for the job titled
          <BoldText>{data?.jobId?.title} </BoldText>
          for the requirement submitted by
          <BoldText>{data?.hiringCompanyId?.companyName}.</BoldText>
        </span>
      );

    case EnumActivityTracker.SUBMIT_OFFER:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>{data?.hiringCompanyId?.companyName}</BoldText>
          submitted an offer to
          {data?.candidateId?.profilePic ? (
            <img
              src={data?.candidateId?.profilePic}
              className="w-6 h-6 rounded-full"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gray-200 font-semibold flex justify-center items-center">
              {data?.candidateId?.firstName?.charAt(0)}
            </div>
          )}
          <BoldText>
            {data?.candidateId?.firstName} {data?.candidateId?.lastName}
          </BoldText>
          for the requirement titled
          <BoldText>{data?.jobRequirementId?.title}.</BoldText>
        </span>
      );
    case EnumActivityTracker.JOB_POSTED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>You</BoldText>
          posted a new job titled
          <BoldText>{data?.jobId?.title}</BoldText>
        </span>
      );
    case EnumActivityTracker.JOB_MATCHES:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <span>
            <BoldText> {data?.metadata?.totalMatchedCandidates}</BoldText> candidates have been
            matched for Job post titled
          </span>
          <BoldText>{data?.jobId?.title}</BoldText>
        </span>
      );
    case EnumActivityTracker.PROFILE_UPDATED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>You </BoldText>
          successfully updated your skills.
        </span>
      );
    case EnumActivityTracker.JOB_REQUIREMENT_POSTED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>{data?.hiringCompanyId?.companyName}</BoldText>
          <span>has posted a job for your requirement titled</span>
          <BoldText>{data?.jobRequirementId?.title}.</BoldText>
        </span>
      );
    case EnumActivityTracker.SUCCESSFUL_PLACEMENT:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>
            {data?.candidateId?.firstName} {data?.candidateId?.lastName}
          </BoldText>
          <span>has been successfully placed at</span>
          <BoldText>{data?.hiringCompanyId?.companyName}</BoldText>
          for a requirement titled
          <BoldText>{data?.jobRequirementId?.title}.</BoldText>
        </span>
      );
    case EnumActivityTracker.INTERVIEW_INVITATION:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>{data?.hiringCompanyId?.companyName}</BoldText>
          <span>has requested an interview with</span>
          <BoldText>
            {data?.candidateId?.firstName} {data?.candidateId?.lastName}
          </BoldText>
          for a requirement titled
          <BoldText>{data?.jobRequirementId?.title}.</BoldText>
        </span>
      );

    case EnumActivityTracker.OFFER_RECEIVED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>{data?.recruiterAgencyId?.companyName} </BoldText>
          sent you an offer from{" "}
          <BoldText>{data?.hiringCompanyId?.companyName} </BoldText>
          regarding
          <BoldText>{data?.jobId?.title}.</BoldText>
        </span>
      );
    case EnumActivityTracker.INTERVIEW_INVITE_SENT:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>ABC Recruitment </BoldText>
          has sent you an <BoldText>interview invitation </BoldText>
          for
          <BoldText>11am Monday 20th October 2025.</BoldText>
        </span>
      );
    case EnumActivityTracker.MEETING_FORWARDED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>{data?.recruiterAgencyId?.companyName} </BoldText>
          has sent you an <BoldText>availability poll. </BoldText>
        </span>
      );
    case EnumActivityTracker.CANDIDATE_SHORTLISTED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>Google Inc </BoldText>
          has shortlisted your application for the position
          <BoldText>React Frontend Developer.</BoldText>
        </span>
      );
    case EnumActivityTracker.SHORTLIST_BY_RECRUITER_AGENCY:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>{data?.recruiterAgencyId?.companyName}</BoldText>
          has shortlisted your application for the position
          <BoldText>{data?.jobId?.title}.</BoldText>
        </span>
      );
    case EnumActivityTracker.APPLICATION_SUBMITTED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>{data?.recruiterAgencyId?.companyName}</BoldText>
          has submitted your application to
          <BoldText>{data?.hiringCompanyId?.companyName}</BoldText>
          for the position titled <BoldText>{data?.jobId?.title}.</BoldText>
        </span>
      );
    case EnumActivityTracker.APPLICATION_VIEWED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>{data?.recruiterAgencyId?.companyName} </BoldText>
          has viewed your application for the position
          <BoldText>{data?.jobId?.title}. </BoldText>
        </span>
      );
    case EnumActivityTracker.PROFILE_UPDATED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>You </BoldText>
          successfully updated your skills.
        </span>
      );
    case EnumActivityTracker.USER_REGISTERED:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>You </BoldText>
          registered successfully on COMTRACKED.
        </span>
      );
    default:
      return (
        <span className="text-xs gap-1 flex flex-wrap text-light-black">
          <BoldText>ABC Recruitment </BoldText>
          sent you an offer from <BoldText>ABC Recruitment </BoldText>
          regarding
          <BoldText>ABC Recruitment.</BoldText>
        </span>
      );
  }
}

function getStatus(type: EnumActivityTracker) {
  switch (type) {
    case EnumActivityTracker.JOB_MATCHES:
      return 'Matches';
    case EnumActivityTracker.JOB_POSTED:
      return 'Job Posted';
    case EnumActivityTracker.CANDIDATE_APPLIED:
      return 'Applied';
    case EnumActivityTracker.MEETING_FORWARDED:
      return 'Availability Poll';
    case EnumActivityTracker.CANDIDATE_SHORTLISTED:
      return 'Shortlisted';
    case EnumActivityTracker.SHORTLIST_BY_RECRUITER_AGENCY:
      return 'Shortlisted';
    case EnumActivityTracker.APPLICATION_DECLINE_BY_RECRUITER_AGENCY:
      return 'Application Declined';
    case EnumActivityTracker.APPLICATION_SUBMITTED:
      return 'Application Submitted';
    case EnumActivityTracker.APPLICATION_VIEWED:
      return 'Application Viewed';
    case EnumActivityTracker.CANDIDATE_APPLIED:
      return 'Applied';
    case EnumActivityTracker.PROFILE_UPDATED:
      return 'Profile Edit';
    case EnumActivityTracker.USER_REGISTERED:
      return 'Registration';
    case EnumActivityTracker.JOB_REQUIREMENT_POSTED:
      return 'Requirement Posted';
    default:
      return type;
  }
}


type CardProps = {
  type: EnumActivityTracker;
  data: IActivityTracker;
};

const Card = ({ data }: CardProps) => {
  return (
    <div className="grid grid-cols-[0.45fr_1.55fr] gap-8 items-center">
      <div className="flex flex-col gap-1.5">
        <span className="text-xss font-semibold text-charcoal">
          {dayjs(data?.createdAt).format("hh:mm a")}
        </span>
        <span className="text-xss text-light-black">
          {dayjs(data?.createdAt).format("ddd Do")}{" "}
          {dayjs(data?.createdAt).format("MMMM YYYY")}
        </span>
        <div className="flex border-l-3 font-semibold text-charcoal text-xss border-l-[3px] border-l-[#6CABDD] border rounded-sm px-2 py-1">
          {getStatus(data?.eventType as EnumActivityTracker)}
        </div>
      </div>
      <div className="flex flex-col justify-start text-xs font-medium text-charcoal border-b h-28 leading-6 pt-10">
        {getDescription(data?.eventType as EnumActivityTracker, data)}
      </div>
    </div>
  );
};

const LIMIT = 15;

const RecruiterTrackerPage = () => {
  const scrollListRef = useRef(null)
  const sentinelRef = useRef(null)
  const [acitvities, setAcitvities] = useState([]);
  const [page, setPage] = useState(1)

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false)
  const [hasMoreData, setHasMoreData] = useState(false)

  const fetchActivityTracker = useCallback(
    async () => {
      try {
        if (page === 1) setIsLoading(true);
        const { data, success } = await getActivityTracker({
          page,
          limit: LIMIT
        });
        if (success && data) {
          setAcitvities((prev) => page === 1 ? data?.data : [...prev, ...data?.data]);
          setHasMoreData(data?.pagination?.totalPages > page)
        }
      } catch (error) {
        console.error("Error: fetching activity trackers", error);
      } finally {
        setIsLoading(false);
        setIsLoadingMoreData(false)
      }
    }, [page]
  );

  const handleLoadMore = useCallback(
    () => {
      if (!hasMoreData || isLoading || isLoadingMoreData) return
      setIsLoadingMoreData(true)
      setPage((prev) => prev + 1)
    },
    [hasMoreData, isLoading, isLoadingMoreData]
  )

  useEffect(() => {
    fetchActivityTracker();
  }, [fetchActivityTracker]);

  useEffect(() => {
    if (!sentinelRef?.current || !scrollListRef?.current) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        handleLoadMore()
      }
    }, {
      root: scrollListRef?.current,
      rootMargin: '0px 0px 20px 0px',
      threshold: 0
    })
    observer.observe(sentinelRef?.current)
    return () => {
      observer.disconnect()
    };
  }, [handleLoadMore])

  return (
    <div className="flex flex-col flex-1 min-h-0 rounded-md bg-white pt-7">
      <span className="font-bold text-charcoal mx-auto">Activity Tracker</span>

      {/* Tracker List */}
      <div
        ref={scrollListRef}
        className="flex flex-col flex-1 min-h-0 mt-4 w-[70%] mx-auto overflow-y-scroll no-scrollbar">
        {isLoading ? (
          <div className="m-auto">
            <LoadingAnimation color="black" className="w-6 h-6" />
          </div>
        ) : acitvities?.length ? (
          <>
            {
              acitvities?.map((activity) => {
                return <Card type={activity?.eventType} data={activity} />;
              })
            }
            {
              isLoadingMoreData ? <div className="flex items-center justify-center py-4 gap-2">
                <LoadingAnimation color="black" className="w-5 h-5" />
                <span className="font-medium text-xs italic text-light-black">Loading more data</span>
              </div>
                :
                null
            }
            {/* Sentinel Observer */}
            <div ref={sentinelRef} className="h-px shrink-0" />
          </>
        ) : (
          <div className="flex-col gap-3 m-auto flex-1 flex justify-center items-center font-medium text-light-black text-sm">
            <TrackIcon className={"w-16 h-16"} fill={"gray"} />
            No acitvities recorded till now.
          </div>
        )}
      </div>
    </div>
  );
};

export default RecruiterTrackerPage;
